import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Users, FileText, TrendingUp, Clock, Package2, CheckCircle, XCircle, LogIn } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { adminAPI } from "@/lib/api";
import { toast } from "sonner";

function formatEur(val) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(val || 0);
}

const BAR_COLORS = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd", "#818cf8", "#6366f1", "#4f46e5"];

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const accessTenant = async (tenantId, tenantName) => {
    try {
      await adminAPI.accessTenant(tenantId);
      toast.success(`Acces a "${tenantName}"`);
      window.location.href = "/";
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  const kpis = [
    { label: "Sociétés actives", value: data?.total_tenants_active || 0, icon: Building2, color: "text-indigo-600", bg: "bg-indigo-50" },
    { label: "Utilisateurs", value: data?.total_users || 0, icon: Users, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Documents", value: data?.global_documents || 0, icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Clients", value: data?.global_clients || 0, icon: Users, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  const revenueKpis = [
    { label: "CA global encaisse", value: formatEur(data?.global_revenue), icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "En attente", value: formatEur(data?.global_pending), icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  const tenants = data?.tenants || [];
  const activeTenants = tenants.filter(t => t.active);

  const chartData = activeTenants
    .filter(t => t.revenue > 0 || t.pending > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8)
    .map(t => ({ name: t.name.length > 15 ? t.name.slice(0, 15) + "..." : t.name, revenue: t.revenue, pending: t.pending }));

  return (
    <div className="page-container" data-testid="superadmin-dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Administration globale
        </h1>
        <p className="text-slate-500 mt-1">Vue d'ensemble de toutes les sociétés</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="p-4 border border-slate-200 rounded-lg bg-white" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                <kpi.icon size={20} className={kpi.color} />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{kpi.label}</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Inter', sans-serif", fontVariantNumeric: "tabular-nums" }}>{kpi.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {revenueKpis.map((kpi) => (
          <Card key={kpi.label} className="p-5 border border-slate-200 rounded-lg bg-white">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                <kpi.icon size={20} className={kpi.color} />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{kpi.label}</p>
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Inter', sans-serif", fontVariantNumeric: "tabular-nums" }}>{kpi.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Revenue Chart per tenant */}
      {chartData.length > 0 && (
        <Card className="p-6 border border-slate-200 rounded-lg bg-white mb-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4">CA par société</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatEur(v)} labelStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Encaisse" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, i) => <Cell key={entry.name || `bar-${i}`} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              </Bar>
              <Bar dataKey="pending" name="En attente" fill="#fbbf24" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Tenants Table */}
      <Card className="p-6 border border-slate-200 rounded-lg bg-white">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-4">Toutes les sociétés</h2>
        <div className="responsive-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Société</TableHead>
                <TableHead>Propriétaire</TableHead>
                <TableHead className="text-center">Utilisateurs</TableHead>
                <TableHead className="text-center">Clients</TableHead>
                <TableHead className="text-center">Documents</TableHead>
                <TableHead className="text-right">CA encaisse</TableHead>
                <TableHead className="text-right">En attente</TableHead>
                <TableHead className="text-center">Modules</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map(t => (
                <TableRow key={t.id} className="table-row-hover" data-testid={`tenant-row-${t.slug}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-slate-400">{t.slug}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{t.owner_email || "-"}</TableCell>
                  <TableCell className="text-center">{t.user_count}</TableCell>
                  <TableCell className="text-center">{t.client_count}</TableCell>
                  <TableCell className="text-center">{t.document_count}</TableCell>
                  <TableCell className="text-right font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>{formatEur(t.revenue)}</TableCell>
                  <TableCell className="text-right" style={{ fontVariantNumeric: "tabular-nums" }}>{formatEur(t.pending)}</TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">
                      {(t.modules || []).length}/7
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {t.active ? (
                      <CheckCircle size={16} className="text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle size={16} className="text-red-400 mx-auto" />
                    )}
                  </TableCell>
                  <TableCell>
                    {t.active && (
                      <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 text-xs" onClick={() => accessTenant(t.id, t.name)} data-testid={`access-${t.slug}`}>
                        <LogIn size={14} className="mr-1" /> Acceder
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Aucune société</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
